<script setup lang="ts">
import DateTimeBlock from '@/components/shared/blocks/DateTimeBlock.vue';
import DescriptionBlock from '@/components/shared/blocks/DescriptionBlock.vue';
import ParticipantsBlock from '@/components/shared/blocks/ParticipantsBlock.vue';
import BookingEntryOverlay from '@/views/booking/BookingEntryOverlay.vue';
import { ref } from 'vue';
import BlocksWrapper from '@/components/shared/blocks/BlocksWrapper.vue';
import {
  IBookingResource,
  IStandardBooking,
  useBookingResourcesStore,
} from '@/stores/bookingResources';

const bookingResourcesStore = useBookingResourcesStore();

interface Props {
  resource: IBookingResource;
}

withDefaults(defineProps<Props>(), {});

const showBookingEntryOverlay = ref<boolean>(
  bookingResourcesStore.selectedBookingEntry?.id ? true : false
);

const showBookingEntry = (booking: IStandardBooking) => {
  showBookingEntryOverlay.value = true;
  bookingResourcesStore.setSelectedBookingEntry(booking);
};

const closeBookingEntryOverlay = () => {
  bookingResourcesStore.setSelectedBookingEntry();
  showBookingEntryOverlay.value = false;
};
</script>

<template>
  <div
    v-if="resource.bookings?.length === 0"
    class="flex justify-center items-center min-h-[500px]"
  >
    <div>
      <div class="flex justify-center">
        <fa-icon
          class="text-neutral-300 text-6xl mb-3"
          :icon="['fas', 'fa-list']"
        />
      </div>

      <p class="h700">{{ $t('emptyStates.BOOKING') }}</p>
    </div>
  </div>

  <div
    v-for="(booking, index) in resource.bookings"
    :key="index"
    class="items-center mb-8 border-1 border-neutral-500 p-12 rounded-[16px] flex space-between w-full bg-white shadow-small"
    @click="showBookingEntry(booking)"
  >
    <div class="flex flex-col flex-1">
      <BlocksWrapper :borderBottom="false">
        <DescriptionBlock
          margin="mb-8"
          :description="booking.description"
          :borderBottom="false"
        />

        <DateTimeBlock
          margin="mb-8"
          :start="booking.start"
          :end="booking.end"
          :borderBottom="false"
        />

        <ParticipantsBlock
          margin="mb-8"
          v-if="booking.participants.length"
          :profiles="booking.participants"
          :hideParticipants="true"
          participant-type="participant"
          icon=""
          :can-trigger-overlay="false"
          :borderBottom="false"
          @click.stop
        />
      </BlocksWrapper>
    </div>

    <div
      class="w-12 h-12 rounded-full bg-neutral-1000 p-2 flex items-center justify-center"
    >
      <fa-icon class="text-3xl text-neutral-0" :icon="['far', 'angle-right']" />
    </div>
  </div>

  <BookingEntryOverlay
    v-if="showBookingEntryOverlay"
    :booking="bookingResourcesStore.selectedBookingEntry"
    @close-overlay="closeBookingEntryOverlay"
  />
</template>
